import React, { useEffect, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import { Flexbox, FlexboxColumn } from '../../components/action/Flexbox';
import ToggleSwitch from '../../components/action/ToggleSwitch';
import TwitterButton from '../../components/action/TwitterButton';
import UploadImage from '../../components/action/UploadImage';
import ButtonApi from '../../components/ButtonApi';
import { useTheme } from '../../components/theme/Theme';
import ThemedText from '../../components/theme/ThemedText';
import ThemeLogo from '../../components/theme/ThemeLogo';
import { useBackendApi } from '../../helpers/backendHelper';
import { useLoginHelper } from '../../helpers/login/loginHelper';
import { useGlobalState } from '../../state/GlobalStateProvider';
import { hasObjectChanged, trackClairtyEvent } from '../../utils';
import './Settings.scss';

const Settings = () => {
  const [{ user, setUser }] = useGlobalState();
  const [theme, toggleTheme] = useTheme();
  const { twitterLogin } = useLoginHelper();
  const backendHelper = useBackendApi();
  const isDark = theme === 'dark';

  const [isEdited, setIsEdited] = useImmer({
    name: false,
    pfp: false,
  });
  const [userData, setUserData] = useImmer();
  const [changed, setChanged] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  function handleChange(event) {
    const { name, value } = event.target;
    setUserData(state => {
      state[name] = value;
    });
  }

  function updatePfp(value) {
    setUserData(state => {
      state['avatarUrl'] = value;
    });
  }

  function handleEdit(name, value) {
    setIsEdited(state => {
      state[name] = value;
    });
  }

  const handleCancel = () => {
    setUserData(user);
    setIsEdited({ name: false, pfp: false });
  };

  const handleSubmit = async () => {
    if (!user) return;
    if (!userData?.name) return alert.error('Hey what should we call you?');
    if (!userData?.avatarUrl) return alert.error('Hey show us how you look');

    const formData = new FormData();
    formData.append('avatarFile', userData?.avatarUrl);
    formData.append('name', userData?.name);
    try {
      const response = await backendHelper.user.updateUserMetadata(formData);

      trackClairtyEvent('setting_change', {
        username: isEdited?.name ? 'change' : 'same',
        pfp: isEdited?.pfp ? 'change' : 'same',
      });

      setUser(response.data);
      setIsEdited({
        name: false,
        pfp: false,
      });
    } catch (error) {
      alert.error('Hmm! Seems there is a problem');
    }
  };

  useEffect(() => {
    setChanged(hasObjectChanged(user, userData));
  }, [userData, user]);

  useEffect(() => {
    if (isEdited.name && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEdited.name]);

  useEffect(() => {
    const handleMessage = event => {
      if (event.data?.type === 'TWITTER_AUTH_SUCCESS') {
        const { user } = event.data;
        if (user) {

          trackClairtyEvent('twitter_connect', {
            connect_place: 'settings',
          });

          setUser(user);
        }
        console.log('Logged in with Twitter:', user);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <FlexboxColumn className='settings_wrapper' gap='2rem' fw alignItems='flex-start'>
      <FlexboxColumn gap='0.5rem' fw alignItems='flex-start'>
        <ThemedText.H4 weight='semi-bold'>App Settings</ThemedText.H4>
        <Flexbox gap='2rem'>
          <FlexboxColumn gap='0.2rem'>
            <Flexbox fw justifyContent='flex-start' gap='0.5rem'>
              <ThemedText.P weight='medium'>Dark Mode</ThemedText.P>
              {/* <Tooltip position={'left'} content={'Change mode'}>
              <ThemeLogo icon={'questionLogo'} />
            </Tooltip> */}
            </Flexbox>
            <ThemedText.P color='color-xlight'>What’s your eyeballs’ take here?</ThemedText.P>
          </FlexboxColumn>
          <ToggleSwitch onChange={toggleTheme} value={isDark} />
        </Flexbox>
      </FlexboxColumn>
      <FlexboxColumn gap='0.5rem' fw alignItems='flex-start'>
        <ThemedText.H4 weight='semi-bold'>User Settings</ThemedText.H4>
        <Flexbox gap='2rem'>
          <FlexboxColumn gap='0.2rem'>
            <Flexbox fw justifyContent='flex-start' gap='0.5rem'>
              <ThemedText.P weight='medium'>Username</ThemedText.P>
              {/* <Tooltip position={'left'} content={'Update your username'}>
              <ThemeLogo icon={'questionLogo'} />
            </Tooltip> */}
            </Flexbox>
            <ThemedText.P color='color-xlight'>This is the main username that will be visible for everyone.</ThemedText.P>
          </FlexboxColumn>
          <Flexbox className={`settings_wrapper_values ${isEdited?.name && 'settings_wrapper_focus'}`} gap='-0.1rem'>
            <input value={userData?.name} disabled={!isEdited?.name} name='name' onChange={handleChange} className='input_size' ref={nameInputRef} />
            <ThemeLogo className={'pointer'} onClick={() => handleEdit('name', !isEdited?.name)} icon={'edit'} />
          </Flexbox>
        </Flexbox>
        <Flexbox gap='2rem'>
          <FlexboxColumn gap='0.2rem'>
            <Flexbox fw justifyContent='flex-start' gap='0.5rem'>
              <ThemedText.P weight='medium'>PFP</ThemedText.P>
              {/* <Tooltip position={'left'} content={'Show how u look like'}>
              <ThemeLogo icon={'questionLogo'} />
            </Tooltip> */}
            </Flexbox>
            <ThemedText.P color='color-xlight'>This is the main profile picture that will be visible for everyone.</ThemedText.P>
          </FlexboxColumn>
          <Flexbox className={`settings_wrapper_values ${isEdited?.pfp && 'settings_wrapper_focus'}`} gap='0.75rem'>
            <UploadImage onImageUpload={updatePfp} value={userData?.avatarUrl} isEdit={isEdited?.pfp} />
            <ThemeLogo className={'pointer'} onClick={() => handleEdit('pfp', !isEdited?.pfp)} icon={'edit'} />
          </Flexbox>
        </Flexbox>
        <Flexbox gap='2rem'>
          <FlexboxColumn gap='0.2rem'>
            <Flexbox fw justifyContent='flex-start' gap='0.5rem'>
              <ThemedText.P weight='medium'>X Account</ThemedText.P>
            </Flexbox>
            <ThemedText.P color='color-xlight'>
              This is the X (Twitter) account connected with your profile. <br />
              <span className='settings_wrapper_visible'>Only visible to you!</span>
            </ThemedText.P>
          </FlexboxColumn>
          {user?.twitterUsername ? (
            <Flexbox className='settings_wrapper_values'>
              <ThemedText.H6>{userData?.twitterUsername}</ThemedText.H6> <ThemeLogo icon={'twitter'} />{' '}
            </Flexbox>
          ) : (
            <TwitterButton onClick={twitterLogin}>
              <ThemedText.H6>Connect Twitter</ThemedText.H6> <ThemeLogo icon={'twitterLogo'} />
            </TwitterButton>
          )}
        </Flexbox>
      </FlexboxColumn>

      {changed && (
        <Flexbox gap='1rem'>
          <ButtonApi onClick={handleSubmit}>Submit</ButtonApi>
          <button onClick={handleCancel}>Cancel</button>
        </Flexbox>
      )}
    </FlexboxColumn>
  );
};

export default Settings;
